import { toNumber } from 'lodash';
import { useEffect, useState } from 'react';
import { Layout } from 'react-grid-layout';
import { useGetLayoutScreens } from 'src/hooks';
import { getScreenName } from 'src/hooks/api/layouts/helpers';
import { useGlobalComponentsScreen } from 'src/hooks/useGlobalComponentsScreen';
import { ILayoutConfig } from 'src/interfaces';
import {
  getGridDimensionCalculations,
  isCurrentScreenWithDYR,
  parseLayoutConfig,
} from 'src/utils';
import { checkSectionTypes } from 'src/utils/section-types-helper';

export const useCreateLayoutsPage = (
  layoutId: number,
  screenIndex: number,
  isRotationScreen?: boolean,
) => {
  const { data: layouts = [], isLoading: isLayoutScreensLoading } =
    useGetLayoutScreens(layoutId, Boolean(layoutId));
  const globalComponentsScreen = useGlobalComponentsScreen(layouts?.[0]);

  const screenName =
    (layouts?.[0]?.layoutType &&
      getScreenName(
        layouts?.[0]?.layoutType,
        layouts?.[0]?.cod_position,
        screenIndex,
        layouts?.[0]?.screens?.length,
        layouts?.[0]?.config?.screenLayoutType,
      )) ||
    '';

  const screenGridLine =
    layouts?.[0]?.config?.gridLines?.[toNumber(screenIndex) - 1] ??
    'vertical3x3Grid';
  const rotationGridLine =
    layouts?.[0]?.config?.rotationGridLines?.[toNumber(screenIndex) - 1] ??
    'vertical3x3Grid';

  const {
    orientation,
    rows,
    cols,
    disclaimerPosition,
    disclaimerType,
  }: ILayoutConfig = parseLayoutConfig(
    isRotationScreen ? rotationGridLine : screenGridLine,
  );

  const {
    gridSectionHeight,
    gridSectionWidth,
    disclaimerHeightOriginal,
    disclaimerHeightScaled,
    disclaimerWidthOriginal,
    extraHeightForDisclaimerTopAdjustment,
    disclaimerWidth,
    gridHeight,
    screenWidth,
    scale,
  } = getGridDimensionCalculations({
    orientation,
    disclaimerPosition,
    disclaimerType,
    rows,
    cols,
  });

  const { hasDYR } = checkSectionTypes(layouts?.[0]?.config?.sectionTypes);

  const isEligibleForRecommendation =
    hasDYR &&
    isCurrentScreenWithDYR(Number(screenIndex), globalComponentsScreen);

  const defaultSectionsInitial = layouts?.[0]?.screens[
    screenIndex - 1
  ].name.includes('placeholder')
    ? isEligibleForRecommendation
      ? [
          {
            height: 440,
            width: 1080,
            left_offset: 0,
            top_offset: 1320 + extraHeightForDisclaimerTopAdjustment,
          },
        ]
      : []
    : layouts?.[0]?.screens[screenIndex - 1].sections.filter(
        (section) =>
          section.height !== disclaimerHeightOriginal &&
          Object.keys(section?.config?.events ?? {}).length === 0,
      );
  const defaultSections = defaultSectionsInitial?.map((section, index) => {
    return {
      h: Math.round(section.height / (gridHeight / rows)),
      w: Math.round(section.width / (screenWidth / cols)),
      x: Math.round((section.left_offset * cols) / screenWidth),
      y: Math.round(
        ((section.top_offset - extraHeightForDisclaimerTopAdjustment) * rows) /
          gridHeight,
      ),
      i: `${index}`,
    };
  });

  const [gridSections, setGridSections] = useState<Layout[]>([]);
  const [updateCounter, setUpdateCounter] = useState<number>(0);

  useEffect(() => {
    if (updateCounter < 2 && defaultSections) {
      setGridSections(defaultSections);
      setUpdateCounter((prev) => prev + 1);
    }
  }, [defaultSections, gridSections, updateCounter]);

  return {
    gridSections,
    setGridSections,
    updateCounter,
    layouts,
    screenName,
    isLayoutScreensLoading,
    gridHeight,
    screenWidth,
    rows,
    cols,
    disclaimerPosition,
    disclaimerType,
    gridSectionHeight,
    gridSectionWidth,
    disclaimerWidth,
    disclaimerWidthOriginal,
    disclaimerHeightScaled,
    defaultSections,
    disclaimerHeightOriginal,
    extraHeightForDisclaimerTopAdjustment,
    scale,
    globalComponentsScreen,
  };
};
